import {RRule, Weekday, type ByWeekday} from 'rrule';
import moment from 'moment-timezone';
import type {Repeat} from 'src/types/node-schedule';
import {UNTIL_OPTIONS, MONTHLY_DAY_OPTIONS} from 'src/utils/node-schedule';

/**
 * Maps weekday string literals to numeric weekday values
 * Used to convert string literals like "MO", "TU" (from CheckboxGroup) to Weekday objects
 * @deprecated Consider refactoring to use Weekday objects directly instead of string conversions
 */
export const WEEKDAY_STRING_TO_NUMBER: Record<string, number> = {
  MO: (RRule.MO as Weekday).weekday,
  TU: (RRule.TU as Weekday).weekday,
  WE: (RRule.WE as Weekday).weekday,
  TH: (RRule.TH as Weekday).weekday,
  FR: (RRule.FR as Weekday).weekday,
  SA: (RRule.SA as Weekday).weekday,
  SU: (RRule.SU as Weekday).weekday,
};

/**
 * Parses an RRule string into a Repeat object for UI editing
 * @param rule - The RRule string to parse (may include "RRULE:" prefix)
 * @returns A Repeat object with all the parsed fields
 */
export const parseRruleToRepeat = (rule: string | undefined): Repeat => {
  if (!rule) {
    return {
      interval: 1,
      freq: RRule.DAILY,
      repeatUntilKey: UNTIL_OPTIONS.INDEFINITELY,
    };
  }

  try {
    // RRule.parseString handles both "RRULE:" prefixed and non-prefixed strings
    const options = RRule.parseString(rule);
    const {interval, freq, until, count, byweekday, bymonthday} = options;
    let repeatUntilKey: string = UNTIL_OPTIONS.INDEFINITELY;
    let monthlyDayKey = MONTHLY_DAY_OPTIONS.SAME_DAY;

    if (freq === RRule.MONTHLY && bymonthday) {
      monthlyDayKey = MONTHLY_DAY_OPTIONS.SPECIFIC_MONTH_DAY;
    } else if (freq === RRule.MONTHLY && byweekday) {
      monthlyDayKey = MONTHLY_DAY_OPTIONS.SPECIFIC_WEEK_DAY;
    }

    let endDate = null;
    if (until) {
      repeatUntilKey = UNTIL_OPTIONS.SPECIFIC_DATE as string;
      endDate = moment(until).format('YYYY-MM-DD');
    } else if (count) {
      repeatUntilKey = UNTIL_OPTIONS.REPEAT_LIMIT as string;
    }

    // RRule.parseString returns Weekday objects, so we can use them directly to keep in store.
    const byweekdayArray: ByWeekday[] | null = Array.isArray(byweekday)
      ? byweekday
      : byweekday
      ? [byweekday]
      : null;

    return {
      interval: interval ?? 1,
      freq: freq ?? RRule.DAILY,
      repeatUntilKey,
      count: count ?? undefined,
      until: endDate,
      monthlyDayKey,
      byweekday: byweekdayArray,
      bymonthday,
    };
  } catch {
    return {
      interval: 1,
      freq: RRule.DAILY,
      repeatUntilKey: UNTIL_OPTIONS.INDEFINITELY,
    };
  }
};

/**
 * Converts a Repeat object to an RRule string
 * @param repeat - The Repeat object to convert
 * @returns An RRule string representation (e.g., "FREQ=DAILY;INTERVAL=1")
 */
export const repeatToRruleString = (repeat: Repeat): string => {
  const rruleOptions: Record<string, any> = {
    freq: repeat.freq ?? RRule.DAILY,
    interval: repeat.interval ?? 1,
  };

  if (repeat.repeatUntilKey === UNTIL_OPTIONS.SPECIFIC_DATE && repeat.until) {
    rruleOptions.until = new Date(repeat.until);
  } else if (
    repeat.repeatUntilKey === UNTIL_OPTIONS.REPEAT_LIMIT &&
    repeat.count
  ) {
    rruleOptions.count = repeat.count;
  }

  if (repeat.freq === RRule.WEEKLY && repeat.byweekday) {
    // byweekday is stored as Weekday objects in UI state, extract .weekday for RRule
    const weekdayArray = Array.isArray(repeat.byweekday)
      ? repeat.byweekday
      : [repeat.byweekday];
    const numbers = weekdayArray.map((wday) => (wday as Weekday).weekday);
    rruleOptions.byweekday = numbers.length === 1 ? numbers[0] : numbers;
  }

  if (
    repeat.freq === RRule.MONTHLY &&
    repeat.monthlyDayKey === MONTHLY_DAY_OPTIONS.SPECIFIC_MONTH_DAY &&
    repeat.bymonthday
  ) {
    rruleOptions.bymonthday = repeat.bymonthday;
  }

  if (
    repeat.freq === RRule.MONTHLY &&
    repeat.monthlyDayKey === MONTHLY_DAY_OPTIONS.SPECIFIC_WEEK_DAY &&
    repeat.byweekday
  ) {
    rruleOptions.byweekday = repeat.byweekday;
  }

  return RRule.optionsToString(rruleOptions);
};
